import { type ReactElement, useMemo } from 'react';

import type { ApiISODateTimeString } from '#/api/types/ApiTypedDate';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SearchAlgoliaJobHit } from '#/modules/search/algolia/types/SearchAlgoliaJobHit';
import { SearchHitImage } from '#/modules/search/components/Hit/Image/SearchHitImage';
import { SearchHitFloatingSaveButton } from '#/modules/search/components/Hit/SaveButton/SearchHitFloatingSaveButton';
import {
  SearchHitCard,
  SearchHitImageHolder,
} from '#/modules/search/components/Hit/SearchHit.styled';
import { SearchHitInteractiveContentWrapper } from '#/modules/search/components/Hit/SearchHitInteractiveContentWrapper';
import type { SearchContextHeaderLocationState } from '#/modules/search/containers/SearchContextHeader/SearchContextHeaderLocationState';
import { getSearchHitElementId } from '#/modules/search/utils/getSearchHitElementId';
import { type SearchSavedListingsStore } from '#/modules/search/zustand-stores/searchSavedListingsStore';
import { getWorkLocationString } from '#/utils/address/getWorkLocationString';
import { formatSalaryRangeWithPeriodHumanName } from '#/utils/listings';

import { SearchJobHitHolder } from './SearchJobHit.styled';
import { SearchJobHitInfo } from './SearchJobHitInfo';

type Props = {
  hit: SearchAlgoliaJobHit;
  href: string;
  trackClick: (() => void) | null;
  showLabel: boolean;
  searchSavedListingsStore: SearchSavedListingsStore | null;
  highlighted: boolean;
  application: {
    status: 'ACTIVE' | 'INACTIVE';
    created: ApiISODateTimeString;
  } | null;
  actions: ReactElement | null;
};

export function SearchJobHit({
  hit,
  href,
  trackClick,
  showLabel,
  searchSavedListingsStore,
  highlighted,
  application,
  actions,
}: Props) {
  const { i18n, locale } = useI18n();

  const {
    name,
    logo,
    city,
    state,
    country,
    published,
    orgName,
    orgType,
    jobType,
    locationType,
    remoteCountry,
    remoteState,
    remoteZone,
    isPostedAnonymously,
    salaryMinimum,
    salaryMaximum,
    salaryPeriod,
    salaryCurrency,
  } = hit;

  const location = useMemo(
    () =>
      getWorkLocationString({
        i18n,
        remoteOptions: {
          remoteZone,
          remoteCountry: remoteCountry || country,
          remoteState: remoteState || state,
        },
        address: {
          city,
          country,
          stateCode: state,
        },
      }),
    [i18n, remoteCountry, remoteState, remoteZone, city, country, state],
  );

  const salaryFormatted = useMemo(
    () =>
      salaryPeriod && (salaryMinimum || salaryMaximum)
        ? formatSalaryRangeWithPeriodHumanName(
            i18n,
            locale,
            salaryMinimum ? parseFloat(salaryMinimum) : null,
            salaryMaximum ? parseFloat(salaryMaximum) : null,
            country,
            salaryCurrency,
            salaryPeriod,
          )
        : '',
    [
      i18n,
      locale,
      salaryMinimum,
      salaryMaximum,
      country,
      salaryCurrency,
      salaryPeriod,
    ],
  );

  return (
    <SearchJobHitHolder
      id={getSearchHitElementId(hit.objectID)}
      data-qa-id="search-result"
    >
      <SearchHitCard>
        <SearchHitInteractiveContentWrapper
          actions={actions}
          highlighted={Boolean(highlighted)}
          listingLinkData={{
            type: 'link-with-state',
            to: href,
            state: {
              showSearchContext: true,
            } satisfies SearchContextHeaderLocationState,
          }}
          onClick={() => trackClick?.()}
        >
          <SearchHitImageHolder $hiddenOnMobile={!logo}>
            <SearchHitImage
              isImported={false}
              imageUrl={null}
              image={null}
              source={null}
              name={name}
              logo={logo}
            />
          </SearchHitImageHolder>

          <SearchJobHitInfo
            application={application}
            jobTypes={jobType}
            isPostedAnonymously={isPostedAnonymously}
            location={location}
            locationType={locationType}
            name={name}
            orgName={orgName}
            orgType={orgType}
            salaryFormatted={salaryFormatted}
            showLabel={showLabel}
            published={published}
          />
        </SearchHitInteractiveContentWrapper>

        {searchSavedListingsStore && (
          <SearchHitFloatingSaveButton
            hit={hit}
            searchSavedListingsStore={searchSavedListingsStore}
          />
        )}
      </SearchHitCard>
    </SearchJobHitHolder>
  );
}
